import { type Vec, dist, distToSegment } from './vec';
import { dedupe } from './polygon';

/**
 * Editable path node: an anchor with optional absolute cubic Bézier handles.
 * A segment between nodes a and b is a cubic curve if a.out or b.in is set, otherwise a straight line.
 */
export interface PathNode {
  x: number;
  y: number;
  in?: Vec;
  out?: Vec;
}

export function cubicPoint(p0: Vec, p1: Vec, p2: Vec, p3: Vec, t: number): Vec {
  const mt = 1 - t;
  const a = mt * mt * mt;
  const b = 3 * mt * mt * t;
  const c = 3 * mt * t * t;
  const d = t * t * t;
  return { x: a * p0.x + b * p1.x + c * p2.x + d * p3.x, y: a * p0.y + b * p1.y + c * p2.y + d * p3.y };
}

/** Adaptive subdivision of a cubic Bézier until the control polygon is within `tol` of the chord. */
export function flattenCubic(p0: Vec, p1: Vec, p2: Vec, p3: Vec, tol: number, out: Vec[], depth = 0): void {
  const flat = Math.max(distToSegment(p1, p0, p3), distToSegment(p2, p0, p3));
  if (flat <= tol || depth >= 16 || dist(p0, p3) < tol * 0.1) {
    out.push(p3);
    return;
  }
  // de Casteljau split at t = 0.5
  const p01 = mid(p0, p1);
  const p12 = mid(p1, p2);
  const p23 = mid(p2, p3);
  const p012 = mid(p01, p12);
  const p123 = mid(p12, p23);
  const m = mid(p012, p123);
  flattenCubic(p0, p01, p012, m, tol, out, depth + 1);
  flattenCubic(m, p123, p23, p3, tol, out, depth + 1);
}

const mid = (a: Vec, b: Vec): Vec => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

/** Converts a node path into a polyline; `closed` adds the closing segment (without repeating the first point). */
export function flattenPath(nodes: PathNode[], closed: boolean, tol = 0.05): Vec[] {
  if (nodes.length === 0) return [];
  const out: Vec[] = [{ x: nodes[0].x, y: nodes[0].y }];
  const count = closed ? nodes.length : nodes.length - 1;
  for (let i = 0; i < count; i++) {
    const a = nodes[i];
    const b = nodes[(i + 1) % nodes.length];
    if (a.out || b.in) {
      flattenCubic(a, a.out ?? a, b.in ?? b, b, tol, out);
    } else {
      out.push({ x: b.x, y: b.y });
    }
  }
  return dedupe(out, closed);
}

export function nodesFromPoints(points: Vec[]): PathNode[] {
  return points.map((p) => ({ x: p.x, y: p.y }));
}

export function mapNodes(nodes: PathNode[], f: (p: Vec) => Vec): PathNode[] {
  return nodes.map((n) => {
    const p = f(n);
    const r: PathNode = { x: p.x, y: p.y };
    if (n.in) r.in = f(n.in);
    if (n.out) r.out = f(n.out);
    return r;
  });
}
